<template>
  <div>
    <v-app-bar flat color="transparent" class="main-appbar">
      <app-bar-nav-icon />
      <v-skeleton-loader
        type="chip"
        transition="fade-transition"
        v-if="loading"
      ></v-skeleton-loader>

      <v-toolbar-title v-else class="headline font-weight-bold"
        >Courses</v-toolbar-title
      >

      <v-spacer></v-spacer>

      <v-row v-show="showSearch" align="center" style="max-width: 300px">
        <v-text-field
          class="search"
          ref="searchField"
          flat
          hide-details
          solo
          rounded
          clearable
          background-color="grey lighten-3"
          label="Search Category"
          v-model="filter.search"
          @input="search"
        ></v-text-field>
      </v-row>

      <v-skeleton-loader
        type="actions"
        transition="fade-transition"
        v-if="loading"
      ></v-skeleton-loader>

      <template v-else>
        <v-select
          class="ml-10"
          hide-details
          dense
          item-text="label"
          item-value="value"
          v-model="defaultFilter"
          :items="filterBy"
          style="max-width: 200px"
          @change="filterCourse"
        />

        <v-btn
          depressed
          class="ml-2"
          :color="sortOrder ? 'primary' : ''"
          @click="
            sortOrder = true
            filterCourse()
          "
        >
          <v-icon>{{ icons.arrowUp }}</v-icon>
        </v-btn>
        <v-btn
          depressed
          class="ml-2"
          :color="!sortOrder ? 'primary' : ''"
          @click="
            sortOrder = false
            filterCourse()
          "
        >
          <v-icon>{{ icons.arrowDown }}</v-icon>
        </v-btn>

        <v-btn class="ml-5" icon @click="showSearchField">
          <img src="@/assets/icons/search.svg" alt height="17.7px" />
        </v-btn>
      </template>
    </v-app-bar>

    <div
      class="mx-4"
      id="venue-list"
      v-infinite-scroll="loadMore"
      :infinite-scroll-disabled="loading"
      infinite-scroll-distance="100"
    >
      <div class="d-flex my-6">
        <v-spacer></v-spacer>
        <template v-if="!loading">
          <v-btn
            color="primary"
            @click="$router.push({ name: 'courses.create' })"
            ><v-icon left dark> {{ icons.add }} </v-icon>
            Add Course
          </v-btn>
        </template>
      </div>
      <v-simple-table>
        <thead>
          <tr>
            <th width="15%" class="text-left">Title</th>
            <th width="15%" class="text-left">Description</th>
            <th width="15%" class="text-left">Product ID</th>
            <th width="15%" class="text-left">Duration</th>
            <th width="15%" class="text-left">Duration Type</th>
            <th width="15%" class="text-left">Subscriber Only</th>
            <th width="15%" class="text-left">Date Created</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="course in courses"
            :key="course.id"
            class="clickable"
            @click="
              $router.push({
                name: 'courses.details',
                params: { id: course.id },
              })
            "
          >
            <td>{{ course.title }}</td>
            <td>{{ course.description }}</td>
            <td>{{ course.product_id }}</td>
            <td>{{ course.duration }}</td>
            <td>{{ course.duration_type }}</td>
            <td>
              <p v-if="!course.subscriber_only" class="red--text mb-0">No</p>
              <p v-if="course.subscriber_only" class="green--text mb-0">Yes</p>
            </td>

            <td>{{ course.addedDate }}</td>
          </tr>
        </tbody>
      </v-simple-table>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import {
  mdiDotsVertical,
  mdiUnfoldMoreHorizontal,
  mdiMagnify,
  mdiAccountCircleOutline,
  mdiChevronRight,
  mdiFileExcel,
  mdiPlus,
  mdiArrowUp,
  mdiArrowDown,
} from '@mdi/js'
import ControlsMixin from '@/utils/mixins/Controls'
import AppBarNavIcon from '@/layouts/shared/AppBarNavIcon'
import infiniteScroll from 'vue-infinite-scroll'
import debounce from 'lodash/debounce'

export default {
  name: 'Course',

  mixins: [ControlsMixin],

  directives: {
    infiniteScroll,
  },

  components: {
    AppBarNavIcon,
  },

  data() {
    return {
      loading: false,
      showSearch: false,
      filter: {
        search: '',
      },
      icons: {
        settings: mdiDotsVertical,
        sort: mdiUnfoldMoreHorizontal,
        search: mdiMagnify,
        view: mdiAccountCircleOutline,
        arrow: mdiChevronRight,
        export: mdiFileExcel,
        add: mdiPlus,
        arrowUp: mdiArrowUp,
        arrowDown: mdiArrowDown,
      },
      snackbar: {
        show: false,
        message: null,
        color: '',
      },
      defaultFilter: 'title',
      sortOrder: true,
      filterBy: [
        {
          value: 'title',
          label: 'Title',
        },
        {
          value: 'duration',
          label: 'Duration',
        },
        {
          value: 'created_at',
          label: 'Date Created',
        },
      ],
    }
  },

  created() {
    this.clearCourse()
  },

  computed: {
    ...mapState({
      courses: (state) => state.course.list,
      listMeta: (state) => state.course.listMeta,
    }),

    paginationLength() {
      return (this.listMeta.total / this.listMeta.per_page) | 0
    },
  },

  mounted() {
    this.fetchCourses()
  },

  methods: {
    ...mapActions({
      getCourses: 'course/getCourses',
    }),

    ...mapMutations({
      clearCourse: 'course/clearCourseList',
    }),

    pageChanged(page) {
      this.fetchCourses(page)
    },

    filterCourse(filterValue) {
      this.clearCourse()
      this.fetchCourses(null, filterValue)
    },

    loadMore() {
      if (
        this.listMeta &&
        this.listMeta.current_page < this.listMeta.last_page
      ) {
        this.fetchCourses(this.listMeta.current_page + 1)
      }
    },

    search: debounce(function () {
      this.clearCourse()
      this.fetchCourses()

      setTimeout(() => {
        this.$refs.searchField.$refs.input.focus()
      }, 500)
    }, 1600),

    async fetchCourses(page = 1) {
      if (this.loading) return
      let params = {
        page,
        sort: this.sortOrder
          ? `${this.defaultFilter}`
          : `-${this.defaultFilter}`,
      }
      if (this.filter.search) {
        params.search = this.filter.search
      }
      this.loading = true
      await this.getCourses(params)
      this.loading = false
    },

    async showSearchField() {
      this.showSearch = !this.showSearch
      await this.$nextTick()
      this.$refs.searchField.$refs.input.focus()
    },
  },
}
</script>
